import React, { useCallback } from 'react';
import { noop } from 'lodash';

import setWrappedDisplayName from 'decorators/setWrappedDisplayName';
import { setLocation } from 'lib/helpers';
import {
	trackContactLinkClick,
	trackOutboundLinkClick,
} from './clickTrackingUtils';

/**
 * Custom hook for click tracking.
 * @param {Object} options
 * @param {Function} [options.onClick] - Original onClick handler
 * @param {string} [options.href] - Link href
 * @param {string} [options.target] - Link target
 * @returns {{ handleClick: Function }}
 */
export const useClickTracking = ({ onClick = noop, href, target } = {}) => {
	const handleClick = useCallback(
		(evt) => {
			// If coreButton, coreLink components that stay within the site
			if (href && target !== '_blank') {
				evt.preventDefault(evt);
				setLocation(href);

				const isTelTag = href.includes('tel:');
				const isMailTag = href.includes('mailto:');

				// If the link or button leads to a number/email
				if (isTelTag || isMailTag) {
					const { target: evtTarget } = evt;
					const { id, className, innerText } = evtTarget;

					let tagType = isTelTag ? 'tel' : 'mailto';

					trackContactLinkClick(
						id || className,
						innerText,
						innerText,
						tagType,
					);
				}
			}

			// if the coreLink/coreButton leads to an external site
			else if (href && target && target.includes('blank')) {
				const { target: evtTarget } = evt;
				const { id, innerText } = evtTarget;

				trackOutboundLinkClick(
					id || 'outbound-link',
					innerText,
					innerText,
					href,
				);
			}

			Promise.resolve(onClick(evt));
		},
		[onClick, href, target],
	);

	return { handleClick };
};

/**
 * HOC wrapper for click tracking (backward compatible).
 */
const withClickTracking = (WrappedComponent) => {
	const WithClickTracking = ({ onClick = noop, ...props }) => {
		const { handleClick } = useClickTracking({
			onClick,
			href: props.href,
			target: props.target,
		});
		return (
			<WrappedComponent
				{...props}
				onClick={onClick}
				handleClick={handleClick}
			/>
		);
	};
	return setWrappedDisplayName('SegmentClickTracking')(WithClickTracking);
};

export default withClickTracking;
